import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import apiClient from "../../api/axiosClient";
import { useNavigate } from "react-router-dom";

const TEMPLATE_FILENAME = "Best Managed Project of the Year Award_2025.xlsx";

const rowMapping = {
  organisationname: 6,

  project_cost: 10,
  final_project_cost: 11,
  // schedule_weightage:16,
  // cost_weightage:17,
  start_date: 17,
  completion_date: 18,
  actual_completion_date: 19,

  schedule_milestone_1: 25,
  actual_milestone_1: 26,
  schedule_milestone_2: 28,
  actual_milestone_2: 29,
  schedule_milestone_3: 31,
  actual_milestone_3: 32,
  schedule_milestone_4: 34,
  actual_milestone_4: 35,
  schedule_milestone_5: 37,
  actual_milestone_5: 38,

  schedule_budget_milestone_1: 43,
  actual_budget_milestone_1: 44,
  schedule_budget_milestone_2: 46,
  actual_budget_milestone_2: 47,
  schedule_budget_milestone_3: 49,
  actual_budget_milestone_3: 50,
  schedule_budget_milestone_4: 52,
  actual_budget_milestone_4: 53,
  schedule_budget_milestone_5: 55,
  actual_budget_milestone_5: 56,

  fatal_accident_rate: 62,
  lost_time_injuries: 65,
  incident_rate: 68,
  man_hours_worked: 69,
  man_hours_worked_contractual: 70,
  carbon_emission: 74,
};

const colLetter = (colIndex) => {
  let letters = "";
  while (colIndex >= 0) {
    letters = String.fromCharCode((colIndex % 26) + 65) + letters;
    colIndex = Math.floor(colIndex / 26) - 1;
  }
  return letters;
};

const colIndexFromLetter = (col) => {
  let index = 0;
  for (let i = 0; i < col.length; i++) {
    index = index * 26 + (col.charCodeAt(i) - 65 + 1);
  }
  return index - 1;
};

const expandSheetRange = (sheet, minCol, minRow, maxCol, maxRow) => {
  const start = `${colLetter(minCol)}${minRow}`;
  const end = `${colLetter(maxCol)}${maxRow}`;
  sheet["!ref"] = `${start}:${end}`;
};

const ExcelBMP = () => {
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exported, setExported] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiClient.get("vw-registration-bmp-submitted/");
        const results = (resp.data && (resp.data.results ?? resp.data)) || [];
        console.log(
          "API results length:",
          results.length,
          "first:",
          results[0]
        );
        setApiData(results);
      } catch (err) {
        console.error("API fetch error:", err);
        setError("Failed to fetch API data. See console.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!loading && !error && apiData && apiData.length > 0 && !exported) {
      (async () => {
        try {
          await performExport(apiData);
          setExported(true);
        } catch (err) {
          console.error("Auto-export failed:", err);
        }
      })();
    }
  }, [loading, error, apiData, exported]);

  const performExport = async (dataToWrite) => {
    if (!dataToWrite || dataToWrite.length === 0) {
      throw new Error("No API data to export.");
    }

    const url = encodeURI(
      `${process.env.PUBLIC_URL || ""}/templates/${TEMPLATE_FILENAME}`
    );
    console.log("Fetching template:", url);
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Template not found (status ${resp.status})`);

    const contentType = resp.headers.get("content-type") || "";
    if (contentType.includes("text/html")) {
      const txt = await resp.text();
      console.error(
        "Template fetch returned HTML (first 300 chars):",
        txt.slice(0, 300)
      );
      throw new Error("Template returned HTML. Check file path/name.");
    }

    const arrayBuffer = await resp.arrayBuffer();
    const magic = new Uint8Array(arrayBuffer.slice(0, 4));
    console.log(
      "Template magic bytes:",
      magic,
      "->",
      String.fromCharCode(...magic)
    );
    if (!String.fromCharCode(...magic).startsWith("PK")) {
      console.warn("Template does not look like xlsx (missing PK).");
    }

    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
      throw new Error("Workbook contains no sheets.");
    }
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    console.log("Using sheet:", sheetName, "initial !ref:", sheet["!ref"]);

    // compute existing bounds
    const existingRef = sheet["!ref"] || "A1:A1";
    const [startRef, endRef] = existingRef.split(":");
    const startColLetter = (startRef || "A1").replace(/[0-9]/g, "");
    const startRow =
      parseInt((startRef || "A1").replace(/[A-Z]/gi, ""), 10) || 1;
    const endColLetter = (endRef || startRef || "A1").replace(/[0-9]/g, "");
    const endRow =
      parseInt((endRef || startRef || "A1").replace(/[A-Z]/gi, ""), 10) || 1;

    let minCol = colIndexFromLetter(startColLetter || "A");
    let minRow = startRow;
    let maxCol = colIndexFromLetter(endColLetter || "A");
    let maxRow = endRow;

    // For each company (row of data), map fields to variables and write into column (G, H, I...)
    dataToWrite.forEach((company, idx) => {
      const colIndex = 9 + idx;
      const col = colLetter(colIndex);
      console.log(`\n--- Writing company #${idx + 1} into column ${col} ---`);

      const organisationname =
        company.organisation_name ?? company.org_name ?? "";
      const final_project_cost =
        company.actual_cost ?? company.final_project_cost ?? "";
      const project_cost = company.estimated_cost ?? company.project_cost ?? "";
      const schedule_weightage =
        company.total_gas_2024 ?? company.schedule_weightage ?? "";
      const cost_weightage =
        company.total_gas_2023 ?? company.cost_weightage ?? "";
      const start_date = company.start_date ?? company.project_start_date ?? "";
      const completion_date =
        company.planned_completion_date ?? company.completion_date ?? "";
      const actual_completion_date = company.actual_completion_date ?? "";

      const schedule_milestone_1 = company.milestone1_schedule ?? "";
      const schedule_milestone_2 = company.milestone2_schedule ?? "";
      const schedule_milestone_3 = company.milestone3_schedule ?? "";
      const schedule_milestone_4 = company.milestone4_schedule ?? "";
      const schedule_milestone_5 = company.milestone5_schedule ?? "";
      const actual_milestone_1 = company.milestone1_actual ?? "";
      const actual_milestone_2 = company.milestone2_actual ?? "";
      const actual_milestone_3 = company.milestone3_actual ?? "";
      const actual_milestone_4 = company.milestone4_actual ?? "";
      const actual_milestone_5 = company.milestone5_actual ?? "";
      const schedule_budget_milestone_1 =
        company.milestone1_budget ?? "";
      const schedule_budget_milestone_2 =
        company.milestone2_budget ?? "";
      const schedule_budget_milestone_3 =
        company.milestone3_budget ?? "";
      const schedule_budget_milestone_4 =
        company.milestone4_budget ?? "";
      const schedule_budget_milestone_5 =
        company.milestone5_budget ?? "";
      const actual_budget_milestone_1 = company.milestone1_actual_amt ?? "";
      const actual_budget_milestone_2 = company.milestone2_actual_amt ?? "";
      const actual_budget_milestone_3 = company.milestone3_actual_amt ?? "";
      const actual_budget_milestone_4 = company.milestone4_actual_amt ?? "";
      const actual_budget_milestone_5 = company.milestone5_actual_amt ?? "";

      const fatal_accident_rate =
        company.fatalities ?? company.fatal_accident_rate ?? "";
      const lost_time_injuries = company.lost_time_injuries ?? "";
      const incident_rate =
        company.osha_incidents ?? company.incident_rate ?? "";
      const man_hours_worked =
        company.man_hours_own ?? company.man_hours_worked ?? "";
      const man_hours_worked_contractual = company.man_hours_contractual ?? "";
      const carbon_emission =
        company.carbon_emission_project ?? company.carbon_emission ?? "";

      const writes = [
        [rowMapping.organisationname, organisationname],
        [rowMapping.project_cost, project_cost],
        [rowMapping.final_project_cost, final_project_cost],
        [rowMapping.schedule_weightage, schedule_weightage],
        [rowMapping.cost_weightage, cost_weightage],
        [rowMapping.start_date, start_date],
        [rowMapping.completion_date, completion_date],

        [rowMapping.schedule_milestone_1, schedule_milestone_1],
        [rowMapping.schedule_milestone_2, schedule_milestone_2],
        [rowMapping.schedule_milestone_3, schedule_milestone_3],
        [rowMapping.schedule_milestone_4, schedule_milestone_4],
        [rowMapping.schedule_milestone_5, schedule_milestone_5],
        [rowMapping.actual_milestone_1, actual_milestone_1],
        [rowMapping.actual_milestone_2, actual_milestone_2],
        [rowMapping.actual_milestone_3, actual_milestone_3],
        [rowMapping.actual_milestone_4, actual_milestone_4],
        [rowMapping.actual_milestone_5, actual_milestone_5],
        [rowMapping.schedule_budget_milestone_1, schedule_budget_milestone_1],
        [rowMapping.schedule_budget_milestone_2, schedule_budget_milestone_2],
        [rowMapping.schedule_budget_milestone_3, schedule_budget_milestone_3],
        [rowMapping.schedule_budget_milestone_4, schedule_budget_milestone_4],
        [rowMapping.schedule_budget_milestone_5, schedule_budget_milestone_5],
        [rowMapping.actual_budget_milestone_1, actual_budget_milestone_1],
        [rowMapping.actual_budget_milestone_2, actual_budget_milestone_2],
        [rowMapping.actual_budget_milestone_3, actual_budget_milestone_3],
        [rowMapping.actual_budget_milestone_4, actual_budget_milestone_4],
        [rowMapping.actual_budget_milestone_5, actual_budget_milestone_5],

        [rowMapping.actual_completion_date, actual_completion_date],
        [rowMapping.fatal_accident_rate, fatal_accident_rate],
        [rowMapping.lost_time_injuries, lost_time_injuries],
        [rowMapping.incident_rate, incident_rate],
        [rowMapping.man_hours_worked, man_hours_worked],
        // [rowMapping.accident_rate_2024, accident_rate_2024],
        [rowMapping.man_hours_worked_contractual, man_hours_worked_contractual],
        [rowMapping.carbon_emission, carbon_emission],
      ];

      writes.forEach(([rowNum, value]) => {
        if (rowNum == null) return; // skip if mapping missing
        const cellRef = `${col}${rowNum}`;
        const pre = sheet[cellRef] ? sheet[cellRef].v : undefined;
        console.log(`pre ${cellRef} =`, pre);

        let writeVal = value;
        if (writeVal === undefined || writeVal === null) {
          writeVal = "";
        }

        try {
          XLSX.utils.sheet_add_aoa(sheet, [[writeVal]], { origin: cellRef });
          const newCell = sheet[cellRef];
          if (newCell) {
            if (typeof writeVal === "number") newCell.t = "n";
            else if (typeof writeVal === "boolean") newCell.t = "b";
            else newCell.t = "s";
          }
        } catch (e) {
          sheet[cellRef] = {
            t: typeof writeVal === "number" ? "n" : "s",
            v: writeVal,
          };
        }

        const post = sheet[cellRef] ? sheet[cellRef].v : undefined;
        console.log(`post ${cellRef} =`, post);

        if (colIndex > maxCol) maxCol = colIndex;
        if (rowNum > maxRow) maxRow = rowNum;
      });
    });

    // update sheet range so Excel shows new cells
    expandSheetRange(
      sheet,
      minCol,
      minRow,
      Math.max(maxCol, minCol),
      Math.max(maxRow, minRow)
    );
    console.log("Updated sheet !ref:", sheet["!ref"]);

    // write workbook and download
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `Modified_${TEMPLATE_FILENAME}`);
    console.log("Excel exported successfully.");
  };

  if (loading) return <div>Loading API data...</div>;
  if (error) return <div style={{ color: "red" }}>{error}</div>;

  return <>{navigate("/Thankyou")}</>;
};

export default ExcelBMP;

// import React, { useEffect, useState } from "react";
// import * as XLSX from "xlsx";
// import { saveAs } from "file-saver";
// import apiClient from "../../api/axiosClient";

// const TEMPLATE_FILENAME = "Best Managed Project of the Year Award_2025.xlsx";

// const rowMapping = {
//   organisationname: 6,

//   project_cost: 10,
//   final_project_cost: 11,

//   // weights / timeline (ensure these match your template - I moved start_date to 15 to avoid duplicate rows)
//   start_date: 15,
//   schedule_weightage: 16,
//   cost_weightage: 17,
//   completion_date: 18,
//   actual_completion_date: 19,

//   fatal_accident_rate: 28,
//   lost_time_injuries: 31,
//   incident_rate: 34,
//   man_hours_worked: 35,
//   man_hours_worked_contractual: 36,
//   carbon_emission: 40,
// };

// const colLetter = (colIndex) => {
//   let letters = "";
//   while (colIndex >= 0) {
//     letters = String.fromCharCode((colIndex % 26) + 65) + letters;
//     colIndex = Math.floor(colIndex / 26) - 1;
//   }
//   return letters;
// };

// const colIndexFromLetter = (col) => {
//   let index = 0;
//   for (let i = 0; i < col.length; i++) {
//     index = index * 26 + (col.charCodeAt(i) - 65 + 1);
//   }
//   return index - 1;
// };

// const expandSheetRange = (sheet, minCol, minRow, maxCol, maxRow) => {
//   const start = `${colLetter(minCol)}${minRow}`;
//   const end = `${colLetter(maxCol)}${maxRow}`;
//   sheet["!ref"] = `${start}:${end}`;
// };

// const isValidDate = (d) => d instanceof Date && !isNaN(d);

// const parseDate = (val) => {
//   // accept Date object, ISO string, or dd-mm-yyyy like strings
//   if (!val && val !== 0) return null;
//   if (val instanceof Date) return isValidDate(val) ? val : null;
//   const maybe = new Date(val);
//   if (isValidDate(maybe)) return maybe;
//   // try more liberal parsing (replace / with -)
//   const normalized = String(val).replace(/\//g, "-");
//   const try2 = new Date(normalized);
//   return isValidDate(try2) ? try2 : null;
// };

// const writeCell = (sheet, cellRef, value) => {
//   if (value === undefined || value === null || value === "") {
//     sheet[cellRef] = { t: "s", v: "" };
//     return;
//   }

//   // dates
//   const d = parseDate(value);
//   if (d) {
//     // XLSX internally stores dates as JS Date when using t:'d'
//     sheet[cellRef] = { t: "d", v: d };
//     return;
//   }

//   // numbers (but avoid turning numeric-looking ids into numbers if they contain leading zeros)
//   if (typeof value === "number") {
//     sheet[cellRef] = { t: "n", v: value };
//     return;
//   }

//   if (typeof value === "string") {
//     const num = value.replace(/,/g, ""); // remove thousand separators
//     if (!Number.isNaN(Number(num)) && num.trim() !== "") {
//       // prefer numeric if the string is purely numeric
//       sheet[cellRef] = { t: "n", v: Number(num) };
//       return;
//     }
//     // otherwise keep string
//     sheet[cellRef] = { t: "s", v: value };
//     return;
//   }

//   if (typeof value === "boolean") {
//     sheet[cellRef] = { t: "b", v: value };
//     return;
//   }

//   // fallback: stringify
//   sheet[cellRef] = { t: "s", v: String(value) };
// };

// const ExcelBMP = () => {
//   const [apiData, setApiData] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [exported, setExported] = useState(false);

//   useEffect(() => {
//     (async () => {
//       try {
//         const resp = await apiClient.get("/vw-digital-submitted/");
//         const results = (resp.data && (resp.data.results ?? resp.data)) || [];
//         console.log("API results length:", results.length, "first:", results[0]);
//         setApiData(results);
//       } catch (err) {
//         console.error("API fetch error:", err);
//         setError("Failed to fetch API data. See console.");
//       } finally {
//         setLoading(false);
//       }
//     })();
//   }, []);

//   useEffect(() => {
//     if (!loading && !error && apiData && apiData.length > 0 && !exported) {
//       (async () => {
//         try {
//           await performExport(apiData);
//           setExported(true);
//         } catch (err) {
//           console.error("Auto-export failed:", err);
//         }
//       })();
//     }
//   }, [loading, error, apiData, exported]);

//   const performExport = async (dataToWrite) => {
//     if (!dataToWrite || dataToWrite.length === 0) {
//       throw new Error("No API data to export.");
//     }

//     const url = encodeURI(`${process.env.PUBLIC_URL || ""}/templates/${TEMPLATE_FILENAME}`);
//     console.log("Fetching template:", url);
//     const resp = await fetch(url);
//     if (!resp.ok) throw new Error(`Template not found (status ${resp.status})`);

//     const contentType = resp.headers.get("content-type") || "";
//     if (contentType.includes("text/html")) {
//       const txt = await resp.text();
//       console.error("Template fetch returned HTML (first 300 chars):", txt.slice(0, 300));
//       throw new Error("Template returned HTML. Check file path/name.");
//     }

//     const arrayBuffer = await resp.arrayBuffer();
//     const magic = new Uint8Array(arrayBuffer.slice(0, 4));
//     console.log("Template magic bytes:", magic, "->", String.fromCharCode(...magic));
//     if (!String.fromCharCode(...magic).startsWith("PK")) {
//       console.warn("Template does not look like xlsx (missing PK).");
//     }

//     const workbook = XLSX.read(arrayBuffer, { type: "array", cellDates: true, dateNF: "yyyy-mm-dd" });
//     if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
//       throw new Error("Workbook contains no sheets.");
//     }
//     const sheetName = workbook.SheetNames[0];
//     const sheet = workbook.Sheets[sheetName];
//     console.log("Using sheet:", sheetName, "initial !ref:", sheet["!ref"]);

//     // compute existing bounds
//     const existingRef = sheet["!ref"] || "A1:A1";
//     const [startRef, endRef] = existingRef.split(":");
//     const startColLetter = (startRef || "A1").replace(/[0-9]/g, "");
//     const startRow = parseInt((startRef || "A1").replace(/[A-Z]/gi, ""), 10) || 1;
//     const endColLetter = (endRef || startRef || "A1").replace(/[0-9]/g, "");
//     const endRow = parseInt((endRef || startRef || "A1").replace(/[A-Z]/gi, ""), 10) || 1;

//     let minCol = colIndexFromLetter(startColLetter || "A");
//     let minRow = startRow;
//     let maxCol = colIndexFromLetter(endColLetter || "A");
//     let maxRow = endRow;

//     dataToWrite.forEach((company, idx) => {
//       const colIndex = 9 + idx; // starting column (J if 0=A)
//       const col = colLetter(colIndex);
//       console.log(`\n--- Writing company #${idx + 1} into column ${col} ---`);

//       // Map API fields -> local variables (update these if your API field names differ)
//       const organisationname = company.organisation_name ?? company.org_name ?? "";
//       const final_project_cost = company.actual_cost ?? company.final_project_cost ?? "";
//       const project_cost = company.estimated_cost ?? company.project_cost ?? "";
//       const schedule_weightage = company.total_gas_2024 ?? company.schedule_weightage ?? "";
//       const cost_weightage = company.total_gas_2023 ?? company.cost_weightage ?? "";
//       const start_date = company.start_date ?? company.project_start_date ?? "";
//       const completion_date = company.planned_completion_date ?? company.completion_date ?? "";
//       const actual_completion_date = company.actual_completion_date ?? "";
//       const fatal_accident_rate = company.fatalities ?? company.fatal_accident_rate ?? "";
//       const lost_time_injuries = company.lost_time_injuries ?? "";
//       const incident_rate = company.osha_incidents ?? company.incident_rate ?? "";
//       const man_hours_worked = company.man_hours_own ?? company.man_hours_worked ?? "";
//       const man_hours_worked_contractual = company.man_hours_contractual ?? "";
//       const carbon_emission = company.carbon_emission_project ?? company.carbon_emission ?? "";

//       const writes = [
//         [rowMapping.organisationname, organisationname],
//         [rowMapping.project_cost, project_cost],
//         [rowMapping.final_project_cost, final_project_cost],
//         [rowMapping.schedule_weightage, schedule_weightage],
//         [rowMapping.cost_weightage, cost_weightage],
//         [rowMapping.start_date, start_date],
//         [rowMapping.completion_date, completion_date],
//         [rowMapping.actual_completion_date, actual_completion_date],
//         [rowMapping.fatal_accident_rate, fatal_accident_rate],
//         [rowMapping.lost_time_injuries, lost_time_injuries],
//         [rowMapping.incident_rate, incident_rate],
//         [rowMapping.man_hours_worked, man_hours_worked],
//         [rowMapping.man_hours_worked_contractual, man_hours_worked_contractual],
//         [rowMapping.carbon_emission, carbon_emission],
//       ];

//       writes.forEach(([rowNum, value]) => {
//         if (rowNum == null) return; // skip if mapping missing
//         const cellRef = `${col}${rowNum}`;
//         const pre = sheet[cellRef] ? sheet[cellRef].v : undefined;
//         console.log(`pre ${cellRef} =`, pre);

//         try {
//           // use helper to set cell with correct type
//           writeCell(sheet, cellRef, value);
//         } catch (e) {
//           console.error("Write failed for", cellRef, e);
//           // fallback
//           sheet[cellRef] = { t: "s", v: value === undefined || value === null ? "" : String(value) };
//         }

//         const post = sheet[cellRef] ? sheet[cellRef].v : undefined;
//         console.log(`post ${cellRef} =`, post);

//         if (colIndex > maxCol) maxCol = colIndex;
//         if (rowNum > maxRow) maxRow = rowNum;
//       });
//     });

//     // update sheet range so Excel shows new cells
//     expandSheetRange(sheet, minCol, minRow, Math.max(maxCol, minCol), Math.max(maxRow, minRow));
//     console.log("Updated sheet !ref:", sheet["!ref"]);

//     // write workbook and download
//     const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array", cellDates: true });
//     const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
//     saveAs(blob, `Modified_${TEMPLATE_FILENAME}`);
//     console.log("Excel exported successfully.");
//   };

//   if (loading) return <div>Loading API data...</div>;
//   if (error) return <div style={{ color: "red" }}>{error}</div>;

//   return (
//     <div>
//       <button
//         onClick={() => {
//           performExport(apiData).catch((e) => {
//             console.error("Manual export error:", e);
//             alert("Export failed — see console for details.");
//           });
//         }}
//       >
//         Export now
//       </button>
//     </div>
//   );
// };

// export default ExcelBMP;
